import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useParams } from "react-router";
import { CurrencyCodes, TimeInterval, TimeSettingsMode } from "@doitintl/cmp-models";
import { type AttributionFilter } from "@doitintl/cmp-models";
import debounce from "lodash/debounce";

import { useAttributionsContext } from "../../../../Context/AttributionsContext";
import { useQueryRun } from "../../../../Pages/CloudAnalytics/previewReport/useQueryRun";
import { getRows } from "../../../../Pages/CloudAnalytics/previewReport/utils";
import { getFilteredAllocationGroups } from "../utils";
import useAllocationDndStore from "./useAllocationDnd";
import { formStoreManager } from "./useAllocationFormState";
import type ReportData from "../../ReportData";

type AllocationStoreMap = {
  [key: string]: {
    filters: AttributionFilter[];
    formula: string;
    name: string;
    id: string | null | undefined;
    isValid: boolean;
  };
};

export const useAllocationQueryRun = () => {
  const { attributions } = useAttributionsContext();
  const { allocationId } = useParams<{ allocationId: string }>();
  const { items, allocationName, unallocatedCosts, hasUnallocated } = useAllocationDndStore();

  const reportDataRef = useRef<ReportData | null>(null);
  const reportData = reportDataRef.current;
  const [formValidationsMap, setFormValidationsMap] = useState<AllocationStoreMap | null>(null);
  const [queryRunning, setQueryRunning] = useState(false);
  const [runQuery, setRunQuery] = useState(false);
  const allocationIds = items.main.map((item) => formStoreManager.getStore(item).getState().values.id as string);

  const getRuleStoreValues = useCallback((items: string[]) => {
    const formStoresMap = items.reduce(
      (acc: AllocationStoreMap, item: string) => ({
        ...acc,
        [item]: {
          id: formStoreManager.getStore(item).getState().values.id,
          filters: formStoreManager.getStore(item).getState().values.filterFields,
          formula: formStoreManager.getStore(item).getState().values.formula,
          name: formStoreManager.getStore(item).getState().values.name,
          isValid: formStoreManager.getStore(item).getState().isValid,
        },
      }),
      {}
    );
    setFormValidationsMap(formStoresMap);
  }, []);

  const subscribeToStores = useCallback((items: string[]) => {
    const subs: (() => void)[] = items.map((item) =>
      formStoreManager.getStore(item).subscribe(({ values, ...rest }) => {
        setFormValidationsMap((prev) => ({
          ...prev,
          [item]: {
            filters: values.filterFields,
            formula: values.formula,
            name: values.name,
            id: values.id,
            isValid: rest.isValid,
          },
        }));
      })
    );
    return subs;
  }, []);

  useEffect(() => {
    if (items.main.length) {
      getRuleStoreValues(items.main);
      const subs = subscribeToStores(items.main);
      return () => {
        subs.forEach((sub) => {
          sub();
        });
      };
    }
  }, [getRuleStoreValues, items.main, items.main.length, subscribeToStores]);

  const allocations = useMemo(() => {
    const allocationArray: Array<{
      id: string | null | undefined;
      name: string;
      formula: string;
      filters: AttributionFilter[];
    }> = [];
    if (!formValidationsMap) {
      return allocationArray;
    }

    for (const value of Object.values(formValidationsMap)) {
      allocationArray.push({
        id: value.id || "draft",
        name: value.name,
        formula: value.formula,
        filters: value.filters,
      });
    }

    return allocationArray;
  }, [formValidationsMap]);

  const debouncedSetRunQuery = useMemo(
    () =>
      debounce(() => {
        setRunQuery(true);
      }, 5000),
    [setRunQuery]
  );

  useEffect(() => {
    const isValid =
      formValidationsMap && Object.keys(formValidationsMap).every((key) => !!formValidationsMap[key].isValid);

    if (isValid) {
      debouncedSetRunQuery();
    } else {
      debouncedSetRunQuery.cancel();
      reportDataRef.current = null;
      setRunQuery(false);
      setQueryRunning(false);
    }

    return () => {
      debouncedSetRunQuery.cancel();
    };
  }, [allocations, debouncedSetRunQuery, formValidationsMap]);

  const attributionGroupsForPreview = useMemo(
    () => [
      {
        id: allocationId,
        name: allocationName,
        attributions: allocationIds,
        ...(hasUnallocated && { nullFallback: unallocatedCosts }),
      },
    ],
    [allocationId, allocationName, allocationIds, hasUnallocated, unallocatedCosts]
  );
  const filteredAttributionGroups = useMemo(
    () => getFilteredAllocationGroups(attributionGroupsForPreview, allocations) || [],
    [attributionGroupsForPreview, allocations]
  );
  useQueryRun({
    attributions,
    previewData: {
      attributionGroupsPayload: filteredAttributionGroups,
      attributionGroups: attributionGroupsForPreview,
      currency: CurrencyCodes.USD,
      timeInterval: TimeInterval.DAY,
      timeRangeOptions: {
        mode: TimeSettingsMode.Last,
        time: TimeInterval.DAY,
        amount: 7,
        includeCurrent: false,
      },
      scope: [],
      rows: getRows(0, filteredAttributionGroups),
      filters: [],
      cols: [],
    },
    queryRunning,
    setQueryRunning,
    reportDataWForecasts: reportDataRef,
    runQuery,
    setRunQuery,
    forecastMode: false,
  });

  return { queryRunning, queryResults: reportData };
};
